import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FileText, Loader2, FileCheck, CalendarDays, Ship, PoundSterling, Receipt } from 'lucide-react';
import { useTaxYear } from '../context/TaxYearContext';
import { useSubscription } from '../context/SubscriptionContext';
import { downloadBase64File, REQUIRED_DOCS } from './reports/reportHelpers';
import { TaxIdentifiersCard } from './reports/TaxIdentifiersCard';
import { DocReadinessCard } from './reports/DocReadinessCard';
import { ReportStats } from './reports/ReportStats';
import { DownloadButtonsSection } from './reports/DownloadButtonsSection';
import { ShareReportDialog } from './reports/ShareReportDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { selectedYear, setSelectedYear } = useTaxYear();
  const { canAddContent } = useSubscription();
  const [stats, setStats] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingZip, setGeneratingZip] = useState(false);
  const [generatingHmrc, setGeneratingHmrc] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareIncludeZip, setShareIncludeZip] = useState(false);
  const [taxIds, setTaxIds] = useState({ ni_number: '', utr: '', date_of_birth: '' });
  const [taxIdsSaving, setTaxIdsSaving] = useState(false);
  const [docReadiness, setDocReadiness] = useState([]);
  const [docNA, setDocNA] = useState({});

  const fetchTaxYears = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/tax-years`);
      if (response.data.tax_years.length > 0 && (!selectedYear || selectedYear === 'all')) {
        setSelectedYear(response.data.tax_years[0]);
      }
    } catch (error) {
      toast.error('Failed to load tax years');
    }
  }, [selectedYear, setSelectedYear]);

  const fetchData = useCallback(async () => {
    if (!selectedYear || selectedYear === 'all') return;

    setLoading(true);
    try {
      const [statsRes, expensesRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats?tax_year=${selectedYear}`),
        axios.get(`${API_URL}/api/expenses`),
        axios.get(`${API_URL}/api/documents?tax_year=${selectedYear}`)
      ]);
      setStats(statsRes.data);
      setExpenses(expensesRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchTaxYears();
  }, [fetchTaxYears]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch tax identifiers + N/A flags
  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/settings/tax-identifiers`),
      axios.get(`${API_URL}/api/settings/doc-na`),
    ]).then(([taxRes, naRes]) => {
      setTaxIds({
        ni_number: taxRes.data.ni_number || '',
        utr: taxRes.data.utr || '',
        date_of_birth: taxRes.data.date_of_birth || '',
      });
      setDocNA(naRes.data.doc_na || {});
    }).catch(() => {});
  }, []);

  // Compute document readiness from uploaded docs for selected tax year
  useEffect(() => {
    if (!documents) { setDocReadiness([]); return; }
    const docs = documents.documents || [];
    const uploadedTypes = new Set(docs.map(d => d.document_type));
    const fileNames = docs.map(d => (d.file_name || '').toLowerCase());

    const readiness = REQUIRED_DOCS.map(req => {
      let found = uploadedTypes.has(req.key) || (req.altKeys || []).some(k => uploadedTypes.has(k));
      if (!found && req.namePatterns) {
        found = fileNames.some(fn => req.namePatterns.some(p => fn.includes(p)));
      }
      return { ...req, uploaded: found };
    });
    setDocReadiness(readiness);
  }, [documents]);

  const handleSaveTaxIds = async () => {
    setTaxIdsSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/tax-identifiers`, taxIds);
      toast.success('Tax details saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setTaxIdsSaving(false);
    }
  };

  const handleToggleNA = async (docKey) => {
    const newVal = !docNA[docKey];
    setDocNA(prev => ({ ...prev, [docKey]: newVal || undefined }));
    try {
      await axios.put(`${API_URL}/api/settings/doc-na`, { doc_key: docKey, not_applicable: newVal });
    } catch {
      setDocNA(prev => ({ ...prev, [docKey]: !newVal || undefined }));
    }
  };

  const generateReport = async () => {
    if (!selectedYear) { toast.error('Please select a tax year'); return; }
    setGenerating(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/generate?tax_year=${selectedYear}`);
      downloadBase64File(response.data.pdf_content, response.data.filename, 'application/pdf');
      toast.success('Tax Summary Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const downloadZipPackage = async () => {
    if (!selectedYear) { toast.error('Please select a tax year'); return; }
    setGeneratingZip(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/export-package?tax_year=${selectedYear}`);
      downloadBase64File(response.data.zip_content, response.data.filename, 'application/zip');
      const c = response.data.contents;
      toast.success(`Tax Package downloaded! Includes PDF report, ${c.trips_csv} trips, ${c.expenses_csv} expenses, and ${c.documents} documents.`);
    } catch (error) {
      toast.error('Failed to generate tax package');
    } finally {
      setGeneratingZip(false);
    }
  };

  const downloadHmrcPackage = async () => {
    if (!selectedYear) { toast.error('Please select a tax year'); return; }
    setGeneratingHmrc(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/hmrc-package?tax_year=${selectedYear}`);
      downloadBase64File(response.data.zip_content, response.data.zip_filename, 'application/zip');
      const s = response.data.summary;
      toast.success(`HMRC Report Pack downloaded! Includes HS205 worksheet, ${s.trips_count} trips, and ${s.wage_slips} wage slips.`);
    } catch (error) {
      toast.error('Failed to generate HMRC package');
    } finally {
      setGeneratingHmrc(false);
    }
  };

  const downloadExcel = async () => {
    if (!selectedYear) { toast.error('Please select a tax year'); return; }
    setGeneratingExcel(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/export-excel?tax_year=${selectedYear}`);
      downloadBase64File(response.data.excel_content, response.data.filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      toast.success(`Excel workbook downloaded with ${response.data.sheets.length} sheets`);
    } catch (error) {
      toast.error('Failed to generate Excel');
    } finally {
      setGeneratingExcel(false);
    }
  };

  const handleShare = async () => {
    if (!shareEmail) { toast.error('Please enter an email address'); return; }
    setSharing(true);
    try {
      const response = await axios.post(`${API_URL}/api/reports/share`, {
        tax_year: selectedYear,
        recipient_email: shareEmail,
        recipient_name: shareName || 'Accountant',
        include_zip: shareIncludeZip,
        message: shareMessage || null
      });
      if (response.data.success) {
        toast.success(`Report sent to ${shareEmail}`);
        setShareOpen(false);
        setShareEmail('');
        setShareName('');
        setShareMessage('');
        setShareIncludeZip(false);
      } else {
        toast.error(response.data.message || 'Failed to send report');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSharing(false);
    }
  };

  const expensesSummary = expenses?.summary || {};
  const documentsCount = documents?.documents?.length || 0;
  const anyBusy = generating || generatingZip || generatingHmrc || generatingExcel;

  return (
    <div className="space-y-6 w-full min-w-0" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">End of Year Reports</h1>
          <p className="text-slate-400 mt-1">Generate comprehensive tax summaries for HMRC or your accountant</p>
        </div>
        {selectedYear && selectedYear !== 'all' && (
          <Badge className="bg-amber-600 text-white text-sm px-3 py-1">
            Tax Year {selectedYear}
          </Badge>
        )}
      </div>

      {selectedYear && selectedYear !== 'all' ? (
        <>
          <TaxIdentifiersCard taxIds={taxIds} setTaxIds={setTaxIds} onSave={handleSaveTaxIds} saving={taxIdsSaving} />

          <DocReadinessCard selectedYear={selectedYear} docReadiness={docReadiness} docNA={docNA} onToggleNA={handleToggleNA} />

          {/* What's Included Section */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <FileCheck className="h-5 w-5 text-amber-500" />
                Your Tax Summary Report Includes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <div className="flex items-center gap-2 text-slate-300 text-sm">
                  <CalendarDays className="h-4 w-4 text-blue-400 shrink-0" />
                  <span>Qualifying Days Breakdown</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300 text-sm">
                  <Ship className="h-4 w-4 text-blue-400" />
                  <span>Complete Trip History</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300 text-sm">
                  <PoundSterling className="h-4 w-4 text-amber-400" />
                  <span>Earnings by Contractor</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300 text-sm">
                  <Receipt className="h-4 w-4 text-amber-400" />
                  <span>Tax Deductions & Expenses</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Report Card */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-500" />
                Tax Year Report — {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
                </div>
              ) : stats ? (
                <div className="space-y-6">
                  <ReportStats stats={stats} expensesSummary={expensesSummary} documentsCount={documentsCount} />

                  <DownloadButtonsSection
                    selectedYear={selectedYear} canAddContent={canAddContent} anyBusy={anyBusy}
                    generating={generating} generatingZip={generatingZip}
                    generatingHmrc={generatingHmrc} generatingExcel={generatingExcel}
                    onGenerateReport={generateReport} onDownloadZip={downloadZipPackage}
                    onDownloadHmrc={downloadHmrcPackage} onDownloadExcel={downloadExcel}
                    onOpenShare={() => setShareOpen(true)} />
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No data available for this tax year</p>
                  <p className="text-sm text-slate-500 mt-2">Add trips to generate your tax report</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Select a Tax Year</h3>
            <p className="text-slate-400">Choose a specific tax year from the sidebar to generate your report</p>
          </CardContent>
        </Card>
      )}

      <ShareReportDialog
        open={shareOpen} onOpenChange={setShareOpen} selectedYear={selectedYear} sharing={sharing}
        shareEmail={shareEmail} setShareEmail={setShareEmail}
        shareName={shareName} setShareName={setShareName}
        shareMessage={shareMessage} setShareMessage={setShareMessage}
        shareIncludeZip={shareIncludeZip} setShareIncludeZip={setShareIncludeZip}
        onShare={handleShare} />
    </div>
  );
};

export default Reports;
